const MODULE_SELECTOR = '[mdle_name="module_4"]';
const PRODUCTS_SECTION_ID = 'header-group-module-4-search-results';
const DEBOUNCE_MS = 200;
const SUGGESTIONS_LIMIT = 5;
const PREVIEW_PRODUCTS_LIMIT = 8;

function debounce(fn, ms) {
  let timeoutId;
  const debounced = (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), ms);
  };
  debounced.cancel = () => clearTimeout(timeoutId);
  return debounced;
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatPreviewPrice(price) {
  if (price == null || price === '') return '';
  if (typeof window.formatCurrency === 'function') {
    return window.formatCurrency(price);
  }
  const amount = Number(price);
  if (Number.isNaN(amount)) return '';
  return `$${(amount / 100).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function resolvePredictiveProductVariant(product) {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const productUrl = String(product?.url || '');
  const variantIdMatch = productUrl.match(/[?&]variant=(\d+)/);
  const matchedVariantId = variantIdMatch ? Number(variantIdMatch[1]) : null;

  let variant = null;
  if (matchedVariantId) {
    variant = variants.find((entry) => Number(entry?.id) === matchedVariantId) || null;
  }
  if (!variant && variants.length === 1) {
    variant = variants[0];
  }
  if (!variant) {
    variant = variants.find((entry) => entry?.available) || variants[0] || null;
  }

  let href = variant?.url || product?.url || '#';
  if (variant?.id && href && !/[?&]variant=/.test(href)) {
    const separator = href.includes('?') ? '&' : '?';
    href = `${href}${separator}variant=${variant.id}`;
  }

  // Misma regla que product_card: imagen del producto, no de la variante.
  const image =
    product?.featured_image?.url ||
    product?.image ||
    variant?.featured_image?.url ||
    variant?.image ||
    '';
  // product.price suele ser el mínimo del producto; preferir precio de la variante matched/disponible
  const priceValue = variant?.price ?? product?.price_min ?? product?.price;

  return {
    variant,
    href,
    image,
    priceValue,
  };
}

function initModule4LiveSearch() {
  const module = document.querySelector(MODULE_SELECTOR);
  if (!module) return;

  const searchRoot = module.querySelector('[data-module-4-search]');
  const input = module.querySelector('.header-group-module-4-search-input-input');
  const clearBtn = module.querySelector('[data-module-4-search-clear]');
  const defaultPanel = module.querySelector('[data-module-4-search-default]');
  const featuredSlider = module.querySelector('[data-module-4-search-featured]');
  const livePanel = module.querySelector('[data-module-4-search-live]');
  const suggestionsEl = module.querySelector('[data-module-4-search-suggestions]');
  const resultsLabel = module.querySelector('[data-module-4-search-results-label]');
  const emptyState = module.querySelector('[data-module-4-search-empty]');
  const emptyTerm = module.querySelector('[data-module-4-search-empty-term]');
  const resultsGrid = module.querySelector('[data-module-4-search-results-grid]');
  const termLabel = module.querySelector('[data-module-4-search-term]');
  const viewAll = module.querySelector('[data-module-4-search-view-all]');
  const viewAllBar = module.querySelector('[data-module-4-search-view-all-bar]');
  const searchForm = module.querySelector('.header-group-module-4-search-form');

  if (!searchRoot || !input || !defaultPanel || !livePanel || !resultsGrid) return;

  const searchUrl = searchRoot.dataset.searchUrl || '/search';
  const predictiveSearchUrl =
    searchRoot.dataset.predictiveSearchUrl || window.routes?.predictive_search_url || '/search/suggest';

  let abortController = null;
  let moduleWasActive = module.classList.contains('activate');
  const productsCache = new Map();
  const suggestCache = new Map();

  const notifyContentChange = () => {
    window.dispatchEvent(new CustomEvent('header-module:content-change'));
  };

  const setClearVisible = (visible) => {
    if (!clearBtn) return;
    clearBtn.hidden = !visible;
  };

  const buildSearchHref = (term) => {
    const url = new URL(searchUrl, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    return url.toString();
  };

  const updateViewAllHref = (term) => {
    if (!viewAll) return;
    viewAll.href = buildSearchHref(term);
  };

  const getSuggestJsonUrl = () => {
    const url = new URL(predictiveSearchUrl, window.location.origin);
    if (!url.pathname.endsWith('.json')) {
      url.pathname = `${url.pathname.replace(/\/$/, '')}.json`;
    }
    return url;
  };

  const renderMarkup = (container, markup) => {
    if (!container) return;
    container.innerHTML = '';
    if (!markup) return;
    const fragment = document.createRange().createContextualFragment(markup);
    container.appendChild(fragment);
  };

  const setFeaturedVisible = (visible) => {
    if (!featuredSlider) return;
    featuredSlider.hidden = !visible;
  };

  const showDefault = () => {
    searchRoot.classList.remove('is-live', 'has-results', 'has-suggestions', 'is-preview', 'is-empty');
    defaultPanel.classList.remove('is-hidden');
    livePanel.hidden = true;
    setFeaturedVisible(true);
    if (viewAllBar) viewAllBar.hidden = true;
    notifyContentChange();
  };

  const showLive = (
    term,
    { withProducts = false, withSuggestions = false, showEmpty = false, isPreview = false } = {}
  ) => {
    searchRoot.classList.add('is-live');
    searchRoot.classList.toggle('has-results', withProducts);
    searchRoot.classList.toggle('has-suggestions', withSuggestions);
    searchRoot.classList.toggle('is-preview', isPreview);
    searchRoot.classList.toggle('is-empty', showEmpty);
    defaultPanel.classList.add('is-hidden');
    livePanel.hidden = false;
    setFeaturedVisible(showEmpty);

    if (viewAllBar) viewAllBar.hidden = !withProducts;
    if (termLabel) termLabel.textContent = term;
    if (emptyTerm) emptyTerm.textContent = term;
    updateViewAllHref(term);

    if (resultsLabel) {
      resultsLabel.hidden = !withProducts;
      const termSpan = resultsLabel.querySelector('[data-module-4-search-term]');
      if (termSpan) termSpan.textContent = term;

      const labelPrefix = resultsLabel.firstChild;
      if (labelPrefix?.nodeType === Node.TEXT_NODE) {
        labelPrefix.textContent = withProducts
          ? isPreview
            ? 'Productos sugeridos para “'
            : 'Resultados de búsqueda para “'
          : 'Resultados de búsqueda para “';
      }
    }

    if (emptyState) emptyState.hidden = withProducts || withSuggestions || !showEmpty;

    notifyContentChange();
  };

  const resetSearch = () => {
    input.value = '';
    setClearVisible(false);
    if (abortController) abortController.abort();
    renderMarkup(suggestionsEl, '');
    resultsGrid.innerHTML = '';
    if (emptyState) emptyState.hidden = true;
    showDefault();
  };

  const parseSection = (html, sectionId) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.getElementById(`shopify-section-${sectionId}`) || doc.querySelector(`#shopify-section-${sectionId}`);
  };

  const buildPreviewProductsMarkup = (products) => {
    if (!products.length) return '';

    return products
      .slice(0, PREVIEW_PRODUCTS_LIMIT)
      .map((product) => {
        const title = escapeHtml(product.title || '');
        const vendor = escapeHtml(product.vendor || '');
        const resolved = resolvePredictiveProductVariant(product);
        const productUrl = new URL(resolved.href || '#', window.location.origin).toString();
        const price = formatPreviewPrice(resolved.priceValue);
        const image = resolved.image;
        const variant = resolved.variant;
        const variantsJson = escapeHtml(JSON.stringify(product.variants || []));
        const atcMarkup =
          variant?.available && variant?.id
            ? `<add-to-cart variant-id="${variant.id}" quantity="1" data-variants='${variantsJson}'>
                <form method="post" action="/cart/add">
                  <input type="hidden" name="id" value="${variant.id}">
                  <button type="button" btwn class="collection-main-product-button">Agregar</button>
                </form>
              </add-to-cart>`
            : '';

        return `<div class="header-group-module-4-search-results-cell">
          <div class="collection-main-product">
            <div class="collection-main-product-image-wrapper">
              <a class="collection-main-product-image-link" href="${productUrl}">
                <div class="collection-main-product-image-stack">
                  ${
                    image
                      ? `<img class="collection-main-product-image collection-main-product-image--primary" src="${image}" alt="${title}" loading="lazy">`
                      : ''
                  }
                </div>
              </a>
              ${atcMarkup}
            </div>
            <div class="collection-main-product-info">
              ${vendor ? `<a class="collection-main-product-tags" href="${productUrl}">${vendor}</a>` : ''}
              <a href="${productUrl}" class="collection-main-product-title-link">
                <span class="collection-main-product-title">${title}</span>
              </a>
              <a href="${productUrl}" class="collection-main-product-prices-link">
                <div class="collection-main-product-prices">
                  <span class="collection-main-product-price">${price}</span>
                </div>
              </a>
            </div>
          </div>
        </div>`;
      })
      .join('');
  };

  const buildQuerySuggestionsMarkup = (queries) => {
    if (!queries.length) return '';

    return `<div class="header-group-module-4-search-suggestions">${queries
      .slice(0, SUGGESTIONS_LIMIT)
      .map((query) => {
        const href = buildSearchHref(query.text);
        return `<a href="${href}" class="header-group-module-4-search-suggestion">${query.styled_text}</a>`;
      })
      .join('')}</div>`;
  };

  const fetchSearchProducts = async (term, signal) => {
    const key = `search:${term.toLowerCase()}`;
    if (productsCache.has(key)) return productsCache.get(key);

    const url = new URL(searchUrl, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('section_id', PRODUCTS_SECTION_ID);

    const response = await fetch(url.toString(), { signal });
    if (!response.ok) {
      return { markup: '', hasResults: false, isPreview: false };
    }

    const section = parseSection(await response.text(), PRODUCTS_SECTION_ID);
    const count = Number(section?.querySelector('[data-result-count]')?.dataset.resultCount || 0);
    const cellsMarkup = Array.from(section?.querySelectorAll('.header-group-module-4-search-results-cell') || [])
      .map((cell) => cell.outerHTML)
      .join('');

    const result = {
      markup: cellsMarkup,
      hasResults: cellsMarkup.length > 0 || count > 0,
      isPreview: false,
    };
    productsCache.set(key, result);
    return result;
  };

  const fetchSuggestData = async (term, signal) => {
    const key = `suggest:${term.toLowerCase()}`;
    if (suggestCache.has(key)) return suggestCache.get(key);

    try {
      const url = getSuggestJsonUrl();
      url.searchParams.set('q', term);
      url.searchParams.set('resources[type]', 'query,product');
      url.searchParams.set('resources[limit]', String(PREVIEW_PRODUCTS_LIMIT));
      url.searchParams.set('resources[limit_scope]', 'each');

      const response = await fetch(url.toString(), { signal });
      if (!response.ok) throw new Error(String(response.status));

      const data = await response.json();
      const queries = data?.resources?.results?.queries || [];
      const products = data?.resources?.results?.products || [];

      const result = {
        suggestionsMarkup: buildQuerySuggestionsMarkup(queries),
        hasSuggestions: queries.length > 0,
        previewMarkup: buildPreviewProductsMarkup(products),
        hasPreviewProducts: products.length > 0,
      };
      suggestCache.set(key, result);
      return result;
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      return {
        suggestionsMarkup: '',
        hasSuggestions: false,
        previewMarkup: '',
        hasPreviewProducts: false,
      };
    }
  };

  const settleSearchResults = async (term, signal) => {
    const [searchSettled, suggestSettled] = await Promise.allSettled([
      fetchSearchProducts(term, signal),
      fetchSuggestData(term, signal),
    ]);

    if (searchSettled.status === 'rejected' && searchSettled.reason?.name === 'AbortError') {
      throw searchSettled.reason;
    }

    if (suggestSettled.status === 'rejected' && suggestSettled.reason?.name === 'AbortError') {
      throw suggestSettled.reason;
    }

    const searchResult =
      searchSettled.status === 'fulfilled'
        ? searchSettled.value
        : { markup: '', hasResults: false, isPreview: false };

    const suggestResult =
      suggestSettled.status === 'fulfilled'
        ? suggestSettled.value
        : {
            suggestionsMarkup: '',
            hasSuggestions: false,
            previewMarkup: '',
            hasPreviewProducts: false,
          };

    // Preferir section Liquid (product_card con ATC + modal de variantes).
    // Predictive solo como fallback: su preview no incluye add-to-cart.
    const productsResult = searchResult.hasResults
      ? searchResult
      : suggestResult.hasPreviewProducts
        ? {
            markup: suggestResult.previewMarkup,
            hasResults: true,
            isPreview: true,
          }
        : searchResult;

    return { productsResult, suggestResult };
  };

  const performSearch = async () => {
    const term = input.value.trim();
    if (!term.length) {
      resetSearch();
      return;
    }

    setClearVisible(true);
    showLive(term);

    try {
      if (abortController) abortController.abort();
      abortController = new AbortController();
      const { signal } = abortController;

      const { productsResult, suggestResult } = await settleSearchResults(term, signal);

      if (input.value.trim() !== term) return;

      const withProducts = productsResult.hasResults;
      const withSuggestions = suggestResult.hasSuggestions;

      renderMarkup(suggestionsEl, suggestResult.suggestionsMarkup);
      renderMarkup(resultsGrid, productsResult.markup);

      if (withProducts || withSuggestions) {
        showLive(term, {
          withProducts,
          withSuggestions,
          isPreview: productsResult.isPreview,
        });
        return;
      }

      resultsGrid.innerHTML = '';
      renderMarkup(suggestionsEl, '');
      showLive(term, { withProducts: false, withSuggestions: false, showEmpty: true });
    } catch (error) {
      if (error?.name === 'AbortError') return;
      resultsGrid.innerHTML = '';
      renderMarkup(suggestionsEl, '');
      showLive(term, { withProducts: false, withSuggestions: false, showEmpty: true });
    }
  };

  const debouncedSearch = debounce(performSearch, DEBOUNCE_MS);

  input.addEventListener('input', () => {
    const term = input.value.trim();
    setClearVisible(term.length > 0);

    if (!term.length) {
      debouncedSearch.cancel();
      resetSearch();
      return;
    }

    showLive(term);
    debouncedSearch();
  });

  searchForm.addEventListener('submit', (event) => {
    event.preventDefault();
    debouncedSearch.cancel();
    performSearch();
  });

  clearBtn?.addEventListener('click', () => {
    debouncedSearch.cancel();
    resetSearch();
    input.focus();
  });

  const focusSearchInput = () => {
    if (!module.classList.contains('activate')) return;
    input.focus({ preventScroll: true });
  };

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[open="module_4"]')) return;
    focusSearchInput();
    window.setTimeout(focusSearchInput, 0);
    window.setTimeout(focusSearchInput, 350);
  });

  const moduleObserver = new MutationObserver(() => {
    const isActive = module.classList.contains('activate');
    if (moduleWasActive && !isActive) resetSearch();
    moduleWasActive = isActive;
  });
  moduleObserver.observe(module, { attributes: true, attributeFilter: ['class'] });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initModule4LiveSearch);
} else {
  initModule4LiveSearch();
}
